//REACT
import { useEffect, useState } from "react";
// GRAPHQL
import { useQuery } from "@apollo/client";
import { getFragmentData, gql } from "../../../__generated__";
import { StudentCustomField } from "../../../__generated__/graphql";

// MUI AND DIV COMPONENTS
import { GridColDef, GridNoRowsOverlay, GridRowId, GridRowModel, GridRenderCellParams } from "@mui/x-data-grid";
import { IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { TableFilterOption } from "../../Manage/components/TableFilter";
import { TableToolbarStacked } from "../../Manage/components/TableToolbar";
import customCheckBoxColumn from "../../../components/CustomCheckboxColumn/CustomCheckboxColumn";
import TablePaginated, { FetchMoreCallbackArgs } from "../../Manage/components/TablePaginated/TablePaginated";
import { Close, OpenInNew, Description, FolderShared } from "@mui/icons-material";

// QUERIES AND MUTATIONS
// CONTEXT AND HOOKS
import { useSlideInContext } from "../../../context/SlideInContext";
import usePersonStore from "../../store/personStore";
import usePersonAuthStore from "../../store/personAuthStore"
import { ADD_STUDENT, GET_TRIAGE, BULK_LAUNCH } from "../../components/SlideIn/contentTypes";

import { studentData, useStudentContext } from "../../../context/studentContext";
import { SELECTED_STUDENT_FRAGMENT, StudentDataType } from "../hooks/useSelectedStudent";
import DateCell from "../../../components/DateCell";
// import { DateCell } from "ui";
import { differenceInYears, parseISO } from "date-fns";
import { dateOnlyISO8601 } from "../../../library/date";
import ParentalConsentCell from "./ParentalConsentCell";
export const STUDENTS_PAGE_QUERY = gql(`
  query StudentsPage($query: String, $first: Int, $after: String, $origin:String) {
      students(query: $query, first: $first, after: $after, origin:$origin) {
          pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
          }
          edges {
              cursor
              node {
                ... SelectedStudent_Student
              }
          }
          totalCount
      },
    location {
    customFields{
      id
      type
      name
        }
      totalStudents
      }
  }
`);

const createCustomField = (customField: any): GridColDef => {
    return {
        field: customField.name ?? "missing",
        headerName: customField.name ?? "missing",
        flex: 1,
        sortable: true,
        valueGetter: (value, row) => {
            const studentCustomField = row.studentCustomFields.find((x: StudentCustomField) => x.customField?.id === customField.id)
            return studentCustomField ? studentCustomField.value : null
        },
        renderCell: (params: GridRenderCellParams) => {
            const studentCustomField = params.row.studentCustomFields.find(
                (x: StudentCustomField) => x.customField?.id === customField.id
            ) as StudentCustomField;

            return studentCustomField ? <>{studentCustomField.value}</> : null;
        },
    };
};

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}

const mapEdgesToTable = (data: any) => {
    return {
        ...data.node,
        age: differenceInYears(new Date(), parseISO(dateOnlyISO8601(data.node.dob)))
    };
};

function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {
    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

const DEFAULT_PAGE_SIZE = 100;

const TABLE_COLUMNS: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: 1, sortable: true },
    { field: "lastName", headerName: "Last Name", flex: 1, sortable: true },
    {
        field: "dob", headerName: "Date of Birth", flex: 1, sortable: true,
        renderCell: ({ value }) => {
            return value ? <DateCell value={value.toString()} type={"age"} /> : ""
        }
    },
];

type SelectedAction = "LAUNCH" | "REPORTS" | "DETAILS" | "EXPIRE"

type StudentTableProps = {
    title: string,
    showNew: boolean
    onBack?: () => void,
    onSelectedStudent: (record: any, action: SelectedAction) => void,
    onBulkLaunch?: () => void
};

export const StudentTable = ({ onBack, onSelectedStudent, title, showNew }: StudentTableProps) => {
    const { handleContent } = useSlideInContext();
    const [pageLoading, setPageLoading] = useState<boolean>(false);
    const { updateBulkStudents, bulkStudents } = useStudentContext();
    const defaultLocation = usePersonStore(state => state.company.defaultLocation);
    const personId = usePersonStore(state => state.id);
    const companyConsentPolicy = usePersonStore(state => state.company.consentPolicy);
    const roles = usePersonAuthStore((state) => state.roles);
    const queryOrigin = window.location.pathname.includes("dashboard") ? "DASHBOARD" : "MANAGE"
    const { loading, data, fetchMore } = useQuery(STUDENTS_PAGE_QUERY, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: "",
            first: DEFAULT_PAGE_SIZE,
            origin: queryOrigin
        },
    });

    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[];
        rows: GridRowModel[];
        filteredRows: GridRowModel[];
        selectionModel: (string | number)[];
        // selectionModel: GridRowSelectionModel;
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        };
        status: "NORMAL" | "FILTERING";
    }>({
        columns: [],
        rows: [],
        filteredRows: [],
        selectionModel: [],
        // selectionModel: { type: "include", ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
        status: "NORMAL",
    });
    useEffect(() => {
        if (bulkStudents.crashedStudents.length > 0) {
            const _crashedIds = bulkStudents.crashedStudents.map((student) => student.id)
            updateBulkStudents([], [], [], [])
            handleSelectionModelChange(_crashedIds)
            // NOTE: WE WANT TO CLEAR THIS SO WE CAN CONTINUE ON?
        }
        if (bulkStudents.successfulStudents.length > 0 && bulkStudents.crashedStudents.length < 1) {
            handleSelectionModelChange([])
            updateBulkStudents([], [], [], [])

        }
    }, [bulkStudents.crashedStudents, bulkStudents.successfulStudents, pageTableState.selectionModel]) //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (data) {
            setPageTableState((prevState) => {
                const columns = [...TABLE_COLUMNS];
                data?.location?.customFields.forEach((field: any) => {
                    columns.push(createCustomField(field));
                });
                if (!roles.includes("SUPPORT ADMIN")) {
                    columns.unshift(
                        customCheckBoxColumn({
                            title: "Mass Launch",
                            selectionModel: pageTableState.selectionModel,
                            handleSelectionModelChange: handleSelectionModelChange,
                            handleSelectAll: handleSelectAll,
                            companyConsentPolicy: companyConsentPolicy,
                            totalRowCount: data?.students?.edges?.filter((edge) => isRowSelectable(edge.node)).length ?? 0
                        })
                    )
                }
                if (companyConsentPolicy) {
                    columns.push({
                        field: "parentalConsent",
                        headerName: "Parental Consent",
                        headerAlign: "center",
                        align: "center",
                        sortable: false,
                        width: 75,
                        renderCell: (params) => {
                            return (
                                <ParentalConsentCell parentalConsentStatus={params.row.parentalConsentStatus}
                                    lastSent={params.row?.lastConsentAction?.createdAt}
                                />
                            )
                        }
                    })
                }
                if (!roles.includes("SUPPORT ADMIN")) {
                    columns.push({
                        field: "launch",
                        headerName: "Launch",
                        headerAlign: "center",
                        align: "center",
                        sortable: false,
                        width: 75,
                        renderCell: (params) => {
                            const record = data?.students?.edges?.find((edge) => {
                                const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                                return fragment.id === params.row.id
                            })
                            const studentStatus = params.row.expired
                            return (
                                <Tooltip title={studentStatus ? "Student is Expired" : ""}>
                                    <div>
                                        <IconButton onClick={() => onSelectedStudent(record?.node as StudentDataType, "LAUNCH")}>
                                            <OpenInNew />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            );
                        },
                    });
                }
                if (!roles.includes("OFFICE ADMIN")) {
                    columns.push({
                        field: "reports",
                        headerName: "Reports",
                        headerAlign: "center",
                        align: "center",
                        sortable: false,
                        width: 75,
                        renderCell: (params) => {
                            const record = data?.students?.edges?.find((edge) => {
                                const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                                return fragment.id === params.row.id
                            })

                            return (
                                <IconButton onClick={() => onSelectedStudent(record?.node as StudentDataType, "REPORTS")}>
                                    <Description />
                                </IconButton>
                            );
                        },
                    });
                }

                columns.push({
                    field: "profile",
                    headerName: "Profile",
                    headerAlign: "center",
                    align: "center",
                    sortable: false,
                    width: 75,
                    renderCell: (params) => {
                        const record = data?.students?.edges?.find((edge) => {
                            const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                            return fragment.id === params.row.id
                        })

                        return (
                            <IconButton onClick={() => onSelectedStudent(record?.node as StudentDataType, "DETAILS")}>
                                <FolderShared />
                            </IconButton>
                        );
                    },
                });
                if (showNew && !roles.includes("SUPPORT ADMIN")) {
                    columns.push({
                        field: "remove",
                        headerName: "Remove",
                        headerAlign: "center",
                        align: "center",
                        sortable: false,
                        width: 100,
                        renderCell: (params) => {
                            const record = data?.students?.edges?.find((edge) => {
                                const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                                return fragment.id === params.row.id
                            })
                            return (
                                <Tooltip title={params.row.expired ? "This student is already expired" : ""}>
                                    <div>
                                        <IconButton disabled={params.row.expired} onClick={() => onSelectedStudent(record?.node as StudentDataType, "EXPIRE")}>
                                            <Close
                                                style={{
                                                    backgroundColor: params.row.expired ? "#EEE" : "#8E1C3B",
                                                    borderRadius: "100%",
                                                    color: params.row.expired ? "#999" : "#FFF",
                                                }}
                                            />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            );
                        },
                    });
                }
                const temp = data?.students?.edges?.map(mapEdgesToTable) ?? [];
                // todo: adjust this is needed, right now admins will see their explicitly assigned students on the dashboard and all students on the manage page
                // 02-28-2025
                if (data) {
                    setPageLoading(false)
                }
                const rows = (window.location.pathname.includes("dashboard") && !(roles.includes("OFFICE ADMIN"))) ? temp.filter(x => x.persons.edges.some((edge: { node: { typename: string, id: string } }) => edge.node.id === personId)) : temp;
                if (pageTableState.status === "FILTERING") {
                    //if its filtering we don't want to mess with this, but we also want to call new things if it updates
                    setPageTableState((prevState) => ({
                        ...prevState,
                        rows,
                    }));
                } else {
                    setPageTableState((prevState) => ({
                        ...prevState,
                        filteredRows: [],
                        rows,
                    }));
                }

                return {
                    ...prevState,
                    columns,
                };
            });
        }
    }, [loading, data, pageTableState.selectionModel]);//eslint-disable-line react-hooks/exhaustive-deps

    const handleFilter = (selectedOption: TableFilterOption) => {
        setPageTableState((prevState) => {
            const filteredRows =
                selectedOption === "ACTIVE"
                    ? prevState.rows.filter((x) => !x.expired)
                    : selectedOption === "EXPIRED"
                        ? prevState.rows.filter((x) => x.expired)
                        : selectedOption === "TSCREEN_AGE"
                            ? prevState.rows.filter((x) => (x.age < 10))
                            : selectedOption === "TWEEN_AGE"
                                ? prevState.rows.filter((x) => (x.age > 9 && x.age < 14))
                                : selectedOption === "TEEN_AGE"
                                    ? prevState.rows.filter((x) => (x.age > 13))
                                    : prevState.rows; // Default "ALL"

            return {
                ...prevState,
                status: selectedOption === "ALL" ? "NORMAL" : "FILTERING",
                filteredRows,
            };
        });
    };
    const isRowSelectable = (student: any): boolean => {
        const status = !student.expired //if they are expired it returns true
        const isEmailPresent = Boolean(student.email)
        const maxReached = pageTableState.selectionModel.length < 31
        const isSelectabled = status && isEmailPresent && maxReached
        return isSelectabled
    }
    const handleSelectAll = (selectAll: boolean) => {
        if (selectAll) {
            const selectableRows = pageTableState.rows.filter((row) => isRowSelectable(row))
            // NOTE: WE ARE SELECTING AND CAPPING THIS A 30 PER CHANCE 06-30-2025
            const allIds = selectableRows?.map((row) => row.id as GridRowId).slice(0, 30);
            handleSelectionModelChange(allIds)
        } else {
            handleSelectionModelChange([])
        }
    }
    const handleSelectionModelChange = (values: (string | number)[]) => {
        const selectedIds = Array.from(values)
        const prevSelectedIds = Array.from(pageTableState.selectionModel);
        const assign = selectedIds.filter(x => !prevSelectedIds.includes(x));
        const unassign = prevSelectedIds.filter(x => !selectedIds.includes(x));
        const uniqAssign = assign.filter(x => !pageTableState.selections.prevAssign.includes(x));
        setPageTableState(prevState => {
            const nextAssign = unique(deltaSelections(uniqAssign, unassign, prevState.selections.nextAssign));
            const nextUnassign = deltaSelections(unassign, assign, prevState.selections.unassign);
            const selectionModel = {
                ...prevState,
                selectionModel: values,
                selections: {
                    ...prevState.selections,
                    nextAssign,
                    unassign: nextUnassign,
                    allSelected: unique(selectedIds.concat(nextAssign).filter(x => x !== unassign[0]))
                }
            }
            return selectionModel;
        })
        const _uniqAssign = pageTableState.rows.filter(row => values.includes(row.id)) as studentData[];
        const _unAssign = pageTableState.rows.filter(row => unassign.includes(row.id)) as studentData[];
        updateBulkStudents(_uniqAssign, _unAssign, [], [])
    }
    const handleOnNew = () => {
        handleContent(ADD_STUDENT)
    }

    const handleTriage = () => {
        handleContent(GET_TRIAGE)
    }

    const handleBulkLaunch = () => {
        // TODO: NEED A WAY TO OFF LOAD THESE BUULK STUDENTS AFTER
        handleContent(BULK_LAUNCH)
    }
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        await fetchMore({
            variables: {
                query: variables.query ?? "",
                after: variables.after ?? null,
                first: variables.first ?? DEFAULT_PAGE_SIZE,
                origin: queryOrigin
            }
        }).then((res) => {
            const temp = res?.data?.students?.edges?.map(mapEdgesToTable) ?? [];
            const rows = (window.location.pathname.includes("dashboard") && !(roles.includes("OFFICE ADMIN"))) ? temp.filter(x => x.persons.edges.some((edge: { node: { typename: string, id: string } }) => edge.node.id === personId)) : temp;
            if (pageTableState.status === "FILTERING") {
                //if its filtering we don't want to mess with this, but we also want to call new things if it updates
                setPageTableState((prevState) => ({
                    ...prevState,
                    rows,
                }));
            } else {
                setPageTableState((prevState) => ({
                    ...prevState,
                    filteredRows: [],
                    rows,
                }));
            }
        }).catch(err => {
            console.log("there is an error: ", err)
        })
    }
    // const totalRows =
    //     pageTableState.status === "FILTERING"
    //         ? pageTableState.filteredRows.length
    //         : data?.students?.totalCount ?? 0;
    const totalRows =
        pageTableState.status === "FILTERING"
            ? pageTableState.filteredRows.length
            : data?.students?.totalCount ?? 0;
    return (
        <Paper>
            <TablePaginated
                loading={loading || pageLoading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => (
                    <TableToolbarStacked
                        title={title}
                        subtitle={`@${defaultLocation?.name}`}
                        newButtonLabel="Add new child"
                        searchQuery={searchQuery ?? ""}
                        onNew={showNew ? handleOnNew : undefined}
                        onTriage={handleTriage}
                        onBack={onBack}
                        onFilter={handleFilter}
                        onSearch={onSearch}
                        onSearchClear={onSearchClear}
                        onBulkLaunch={pageTableState.selectionModel.length > 0 ? handleBulkLaunch : undefined}
                    />
                )}
                slots={{
                    noRowsOverlay:
                        (data?.location?.totalStudents ?? 0) <= 0
                            ? NoStudentsFoundOverlay :
                            roles.includes("SUPPORT ADMIN") ? RoleBasedOverlay
                                : GridNoRowsOverlay
                }}

                pageInfo={data?.students?.pageInfo}
                pageSize={DEFAULT_PAGE_SIZE}
                columns={pageTableState.columns}
                // checkboxSelection={true}
                // selectionProps={{
                //     rowSelectionModel: pageTableState.rows.length > 0 ? { type: 'include', ids: new Set(pageTableState.selections.allSelected) } : { type: 'include', ids: new Set() },
                //     onRowSelectionModelChange: handleSelectionModelChange
                // }}
                rows={
                    pageTableState.status === "FILTERING"
                        ? pageTableState.filteredRows
                        : pageTableState.rows
                }
                getRowClassName={(params) => params.row.expired ? "Expired" : "Active"}
                totalRows={totalRows}
                onFetchMore={handleFetchMore}
            />
        </Paper>
    );
};

// function delay(ms: number) {
//     return new Promise((resolve) => setTimeout(resolve, ms));
// }

function NoStudentsFoundOverlay() {
    const roles = usePersonAuthStore(state => state.roles)
    const _location = window.location.pathname.includes("dashboard")
    if (_location) {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                You do not have any children directly associated with your account
                To get started go to "Manage Children" and click "Add new Child" or contact your futuresThrive representative to facilitate a bulk upload.
            </Typography>
        </Stack>)
    } else if (roles.includes("SUPPORT ADMIN")) {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                There are no children at this location. Please contact your admin to add new children or contact your futuresThrive representative to facilitate a bulk upload
            </Typography>
        </Stack>)
    } else
        return (
            <Stack height="100%" alignItems="center" justifyContent="center">
                <Typography width={500}>
                    You do not have any children associated with this location.
                    Select “Add New Child” to get started or contact your futuresThrive representative to facilitate a bulk upload.
                </Typography>
            </Stack>
        )
}

function RoleBasedOverlay() {
    const _location = window.location.pathname.includes("dashboard")
    if (_location) {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                You do not have any children directly associated with your account based on your assigned role.
                To view children at your location(s) please go to "Manage Children" in the menu
            </Typography>
        </Stack>)
    } else {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                There are no children at this location. Please contact your admin to add new children or contact your futuresThrive representative to facilitate a bulk upload
            </Typography>
        </Stack>)
    }
}
